import {
  computed,
  inject,
  Injectable,
  Injector,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { WebDocumentFile } from '../types/web-document';
import { ApiDataAccess, ApiResourceLocation, Dictionary } from '@softline/core';
import { SOFTLINE_API_WEBDOC_FAVORITES } from '../bauprofi.api';
import { handleRequestError } from '@softline/application';
import { deleteFile } from '../functions/delete-file';
import { renameFile } from '../functions/rename-file';

@Injectable()
export class FavoriteService {
  readonly #dataAccess = inject(ApiDataAccess);
  readonly #injector = inject(Injector);

  readonly #remoteFavorites = resource({
    loader: async () => {
      const location = this.createResourceLocation({});
      return (await this.#dataAccess.get<{ result: WebDocumentFile[] }>(location)).result;
    },
  });

  readonly favorites = linkedSignal<WebDocumentFile[] | undefined, WebDocumentFile[]>({
    source: () => this.#remoteFavorites.value(),
    computation: (value) => {
      return value ?? ([] as WebDocumentFile[]);
    },
  });

  readonly isUpdating = signal(false);
  readonly isLoading = computed(() => this.#remoteFavorites.isLoading());
  readonly error = computed(() => this.#remoteFavorites.error());

  isFavorite(file: WebDocumentFile): boolean {
    return !!this.favorites().find((o) => o.url === file.url);
  }

  getResource() {
    return this.#remoteFavorites;
  }

  async toggleFavorite(document: WebDocumentFile): Promise<void> {
    const stored = this.favorites().find(({ url }) => url === document.url);
    await this.setFavorite(document, !stored);
  }

  async setFavorite(document: WebDocumentFile, newValue: boolean): Promise<void> {
    this.isUpdating.set(true);

    if (newValue) {
      await this.markAsFavorite(document);
    } else {
      await this.removeFromFavorites(document);
    }

    this.isUpdating.set(false);
  }

  async reload(): Promise<void> {
    this.#remoteFavorites.reload();
  }

  async delete(document: WebDocumentFile): Promise<void> {
    const current = this.favorites();

    try {
      await deleteFile(this.#dataAccess, document);
      this.favorites.set(this.favorites().filter((o) => o.url !== document.url));
      this.#remoteFavorites.reload();
    } catch (e) {
      this.favorites.set([...current]);
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async rename(document: WebDocumentFile, newTitle: string): Promise<void> {
    const current = this.favorites();

    try {
      await renameFile(this.#dataAccess, {
        fileUrl: document.url,
        newTitle,
      });
      this.favorites.set(this.favorites().filter((o) => o.url !== document.url));
      this.#remoteFavorites.reload();
    } catch (e) {
      this.favorites.set([...current]);
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  private async markAsFavorite(document: WebDocumentFile): Promise<void> {
    const currentFavorites = this.favorites();

    try {
      this.favorites.set([...currentFavorites, document]);
      await this.updateFavoriteStatus(document);
    } catch (e) {
      this.favorites.set([...currentFavorites]);
      handleRequestError(e, this.#injector, {
        show: true,
      });
    }
  }

  private async removeFromFavorites(document: WebDocumentFile): Promise<void> {
    const currentFavorites = this.favorites();

    try {
      this.favorites.set(currentFavorites.filter((o) => o.url !== document.url));
      await this.updateFavoriteStatus(document);
    } catch (e) {
      this.favorites.set([...currentFavorites]);
      handleRequestError(e, this.#injector, {
        show: true,
      });
    }
  }

  private async updateFavoriteStatus(
    document: WebDocumentFile
  ): Promise<WebDocumentFile[]> {
    return await this.#dataAccess.create(this.createResourceLocation({}), {
      fileUrl: document.url,
    });
  }

  private createResourceLocation(queryParams: Dictionary<unknown>): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WEBDOC_FAVORITES,
      queryParams,
    };
  }
}
